import classNames from 'classnames';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React from 'react';

import FacebookIcon from '../../assets/svg/facebook-alternate.inline.svg';
import InstagramIcon from '../../assets/svg/instagram.inline.svg';
import LinkIcon from '../../assets/svg/link.inline.svg';
import LinkedInIcon from '../../assets/svg/linkedin.inline.svg';
import TiktokIcon from '../../assets/svg/tiktok.inline.svg';
import { AlmaLink } from '../alma-link';
import { BrandIcon } from '../brand-icon';
import { PortableText } from '../portableText';
import { SystemIcon } from '../system-icon';
import * as styles from './cards.module.css';

interface CardProps {
	arrowPosition: any;
	buttonColorTheme: any;
	buttonFillBorder: any;
	cardColor: any;
	cardData: any;
	cardPadding: any;
	cardTitleStyle: any;
	cardsPerRow: any;
	contentAlignment: any;
	contentOrder: any;
	fadeEffects: any;
	hasArrow: any;
	iconPosition: any;
	imageAspectRatio: any;
	isMobile: any;
	kind: any;
	linkColor: any;
	textColor: any;
	underlined: any;
}

export function Card({
	arrowPosition,
	buttonColorTheme,
	buttonFillBorder,
	cardColor,
	cardData,
	cardPadding,
	cardTitleStyle,
	cardsPerRow,
	contentAlignment,
	contentOrder,
	fadeEffects,
	hasArrow,
	iconPosition,
	imageAspectRatio,
	isMobile,
	kind,
	linkColor,
	textColor,
	underlined,
}: CardProps) {
	const enableFadeEffect = fadeEffects?.enableFadeEffect;
	const fadeEffectDirection = fadeEffects?.fadeEffectDirection;

	const {
		_rawDescription,
		brandIcon,
		ctaLink,
		ctaOpenInNewTab,
		ctaTitle,
		iconType,
		image,
		link,
		socialLinks,
		subtitle,
		systemIcon,
		title,
	} = cardData;

	// content padding
	const cardPaddingHorizontal = cardPadding?.paddingHorizontal;
	const cardPaddingVertical = cardPadding?.paddingVertical;

	const imageData = getImage(image?.asset);

	// icon definitions
	const useBrandIcon = iconType === 'brandIcon';
	const useSystemIcon = iconType === 'systemIcon';
	const useLeftIconLayout = iconPosition === 'iconLeft';

	const brandIconComponent = useBrandIcon && brandIcon && (
		<div className={styles.cardIcon}>
			<BrandIcon icon={brandIcon} />
		</div>
	);

	const systemIconComponent = useSystemIcon && systemIcon && (
		<div className={styles.cardIcon}>
			<SystemIcon
				icon={systemIcon?.icon}
				iconColor={systemIcon?.iconColor}
				iconSize={systemIcon?.iconSize}
			/>
		</div>
	);

	const dynamicContent = contentOrder?.map((content, index) => {
		let element = null;

		switch (content) {
			case 'icon':
				if (useLeftIconLayout) {
					return null;
				} else if (useBrandIcon && brandIcon) {
					element = (
						<div
							className={styles.cardIcon}
							key={`${content}-${index}`}
						>
							<BrandIcon icon={brandIcon} />
						</div>
					);
				} else if (useSystemIcon && systemIcon) {
					element = (
						<div
							className={styles.cardIcon}
							key={`${content}-${index}`}
						>
							<SystemIcon
								icon={systemIcon?.icon}
								iconColor={systemIcon?.iconColor}
								iconSize={systemIcon?.iconSize}
							/>
						</div>
					);
				} else {
					element = null;
				}
				break;
			case 'image':
				link && imageData
					? (element = (
						<a
							href={link}
							key={`${content}-${index}`}
							rel='noopener noreferrer'
							target='_blank'
						>
							<GatsbyImage
								alt={image?.alt || ''}
								className={classNames(
									imageAspectRatio &&
									styles[imageAspectRatio],
									styles.cardImage
								)}
								image={imageData}
							/>
						</a>
					))
					: imageData
						? (element = (
							<GatsbyImage
								alt={image?.alt || ''}
								className={classNames(
									imageAspectRatio &&
									styles[imageAspectRatio],
									styles.cardImage
								)}
								image={imageData}
								key={`${content}-${index}`}
							/>
						))
						: (element = null);
				break;
			case 'title':
				link
					? (element = (
						<a
							href={link}
							key={`${content}-${index}`}
							rel='noopener noreferrer'
							target='_blank'
						>
							<h3
								className={classNames(
									cardTitleStyle,
									styles.cardTitle,
									'card-title'
								)}
							>
								{title}
							</h3>
						</a>
					))
					: (element = (
						<h3
							className={classNames(
								cardTitleStyle,
								styles.cardTitle,
								'card-title'
							)}
							key={`${content}-${index}`}
						>
							{title}
						</h3>
					));
				break;
			case 'subtitle':
				subtitle
					? (element = (
						<p
							className={styles.cardSubtitle}
							key={`${content}-${index}`}
						>
							<strong>{subtitle}</strong>
						</p>
					))
					: (element = null);
				break;
			case 'description':
				_rawDescription
					? (element = (
						<PortableText
							className={styles.cardPortableText}
							blocks={_rawDescription}
							key={`${content}-${index}`}
						/>
					))
					: (element = null);
				break;
			case 'cta':
				ctaLink && ctaTitle
					? (element = (
						<div className={styles.cardCtaContainer}>
							<AlmaLink
								cta={{
									arrowPosition,
									buttonColorTheme,
									buttonFillBorder,
									hasArrow,
									kind,
									link: ctaLink,
									linkColor,
									openInNewTab: ctaOpenInNewTab,
									title: ctaTitle,
									underlined,
								}}
								key={`${content}-${index}`}
							/>
						</div>
					))
					: (element = null);
				break;
			case 'socialLinks':
				socialLinks && socialLinks?.length > 0
					? (element = (
						<div
							className={classNames(
								styles.socialLinksContainer,
								'socialLinks'
							)}
							key={`${content}-${index}`}
						>
							<p>Personal links:</p>
							<div className={styles.socialLinks}>
								{socialLinks.map((socialLink, index) => {
									const { icon, link } = socialLink;

									return (
										<a
											href={link}
											key={`${icon}-${index}`}
											rel='noopener noreferrer'
											target='_blank'
										>
											{icon === 'instagram' && (
												<InstagramIcon />
											)}
											{icon === 'website' && (
												<LinkIcon />
											)}
											{icon === 'linkedin' && (
												<LinkedInIcon />
											)}
											{icon === 'tiktok' && (
												<TiktokIcon />
											)}
											{icon === 'facebook' && (
												<FacebookIcon />
											)}
										</a>
									);
								})}
							</div>
						</div>
					))
					: (element = null);
				break;
			default:
				element = null;
		}

		return element;
	});

	return (
		<div
			className={classNames(
				'card-container',
				cardColor && `b${cardColor}`,
				cardPaddingHorizontal,
				cardPaddingVertical,
				cardsPerRow && styles[cardsPerRow],
				contentAlignment && styles[contentAlignment],
				!isMobile && enableFadeEffect && 'reveal',
				!isMobile && fadeEffectDirection && fadeEffectDirection,
				styles.cardContainer,
				textColor && `${textColor}`,
				useLeftIconLayout && styles.leftIconLayout
			)}
		>
			{useLeftIconLayout && (brandIconComponent || systemIconComponent) && (
				<div>
					{brandIconComponent}
					{systemIconComponent}
				</div>
			)}
			{useLeftIconLayout ? (
				<div className={styles.cardContent}>
					{dynamicContent}
				</div>
			) : (
				dynamicContent
			)}

		</div>
	);
}
